import { Box, Button, Typography } from "@mui/material";
import { Fragment } from "react";

import { ExtractionDraftValue } from "./types";

export interface RecordDiffTooltipProps {
  /** What the record holds today, in display form. */
  recordDisplay: string;
  /** What this review would write instead, in display form. */
  readingDisplay: string;
  /** Takes the record's value into the field. */
  onTake: (value: ExtractionDraftValue) => void;
  /**
   * Dismisses the disagreement, keeping what the field holds. Not an edit — the
   * value does not change; the reviewer has just said which of the two they want,
   * and the warning, the count and travel stop asking.
   */
  onKeep: () => void;
  recordValue: ExtractionDraftValue;
  /** Appended when the extractor was also unsure, so one icon carries both. */
  lowConfidenceNote?: string;
}

/**
 * Tooltip body for a value that disagrees with the record it is about to
 * replace: the two values side by side, each one clickable to take it into the
 * field.
 *
 * Laid out like `SourcesDisagreeTooltip`, which the manifest review already uses
 * for the same question, so a reviewer meets one idiom for "these two disagree"
 * across the app. It takes the value rather than copying it, because unlike the
 * manifest case the field is right here and editable.
 */
export function RecordDiffTooltip({
  recordDisplay,
  readingDisplay,
  onTake,
  onKeep,
  recordValue,
  lowConfidenceNote,
}: RecordDiffTooltipProps) {
  // One row acts and one reports. The record's value can be written into the
  // field, so it is a button; the document's is already there, so a click on it
  // would write nothing and reading it as an action is what made it look broken.
  // Keeping the document's value is what Dismiss below is for.
  const rows: { label: string; display: string; take?: ExtractionDraftValue }[] = [
    { label: "On the record", display: recordDisplay, take: recordValue },
    { label: "From this document", display: readingDisplay },
  ];

  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
        This would replace what the record holds
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          columnGap: 1.5,
          rowGap: 0.25,
          alignItems: "baseline",
        }}
      >
        {rows.map((row) => (
          <Fragment key={row.label}>
            <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
              {row.label}
            </Typography>
            {"take" in row ? (
              <Box
                component="button"
                type="button"
                onClick={() => onTake(row.take ?? null)}
                aria-label={`Use the value on the record: ${row.display}`}
                title="Click to use this value"
                sx={{
                  appearance: "none",
                  border: "none",
                  background: "none",
                  p: 0,
                  m: 0,
                  font: "inherit",
                  color: "inherit",
                  textAlign: "left",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                <Typography variant="caption" component="span">
                  {row.display}
                </Typography>
              </Box>
            ) : (
              <Typography variant="caption" component="span">
                {row.display}
              </Typography>
            )}
          </Fragment>
        ))}
      </Box>
      {lowConfidenceNote != null && (
        <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.7 }}>
          {lowConfidenceNote}
        </Typography>
      )}
      {/* The other way out: keep what the field holds and stop being asked. Named
          "Dismiss warning" rather than "Dismiss", and kept away from the line
          about clicking values, because directly under it a bare "Dismiss" reads
          as closing the popup. */}
      <Button
        size="small"
        color="inherit"
        onClick={onKeep}
        sx={{ mt: 0.75, px: 0, minWidth: 0, textDecoration: "underline" }}
      >
        Dismiss warning
      </Button>
      <Typography
        variant="caption"
        sx={{ display: "block", mt: 0.5, opacity: 0.7, fontStyle: "italic" }}
      >
        Click a value to apply it
      </Typography>
    </Box>
  );
}
